import { useMatch, NavLink } from "react-router-dom";
import type { Immutable } from "immer";
import { Box, Divider, ListItemButton, ListItemIcon, ListItemText, Typography, useTheme } from "@mui/material";
import type { FunctionComponent } from "react";
import { useSelector } from "react-redux";

import type { RootState } from "../store/state/rootReducer";
import useMixPanel from "../lib/mixpanel";

import { makeStyles, useScreenSize } from "./theme/theme";
import { iconMap } from "./NewDrawerMenu";
import { DrawerMenuSubItem } from "./NewDrawerMenuSubItem";
import type { INavItem } from "./interfaces/interfaces";
import { BadgeBetaFeature } from "./BadgeBetaFeature";

const useMenuItemStyles = makeStyles<{ isSelected: boolean }>()((theme, { isSelected }) => ({
  muiList: {
    padding: "0 !important",
  },
  links: {
    textDecoration: "none",
  },
  listItem: {
    // color: "inherit !important",
    color: "#464C53",
    paddingleft: theme.spacing(2),
    paddingtop: theme.spacing(1.2),
    paddingRight: "8px",
    paddingLeft: "8px",
    borderRadius: "8px",
    paddingbottom: theme.spacing(1.2),
    [theme.breakpoints.down("sm")]: {
      paddingleft: +theme.spacing(2) * theme.mobileScalingFactor * (1 - theme.iconScalingFactor),
      paddingtop: +theme.spacing(1.2) * theme.mobileScalingFactor * (1 - theme.iconScalingFactor),
      paddingbottom: +theme.spacing(1.2) * theme.mobileScalingFactor * (1 - theme.iconScalingFactor),
    },
    "&.Mui-selected": {
      backgroundColor: "#DEF0EE",
    },
    "&:hover": {
      color: "#464C53",
    },
  },
  iconRoot: {
    justifyContent: "center",
    minWidth: 0,
    marginRight: "4px",
    color: isSelected && `#239b8b !important`,
  },
  iconRootCollapsed: {
    width: "100%",
    minWidth: "inherit !important",
  },
  title: {
    color: isSelected ? `#239b8b !important` : "#464C53",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    margin: 0,
  },
  sectionTitle: {
    color: "#606971",
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "16px",
    textTransform: "uppercase",
  },
  sectionDivider: {
    borderColor: "#D9DCDE",
    margin: "20px 16px 24px",
  },
}));

function renderIcon(Icon: FunctionComponent) {
  return <Icon />;
}

export interface IDrawerMenuItemProps {
  item: Immutable<INavItem>;
  isExpanded: boolean;
  idMap: RootState["frame"]["nav"]["itemsById"];
  setIsExpanded: (expanded: boolean) => void;
  alwaysShowChildren: boolean;
}

export function DrawerMenuItem({ item, isExpanded, setIsExpanded, idMap, alwaysShowChildren }: IDrawerMenuItemProps) {
  const newFeatureFlag = useSelector((state: RootState) => !!state.featureFlags?.flags?.newFeatureUi);
  const theme = useTheme();
  const { isSmall } = useScreenSize();

  const match = useMatch(`${item.href}/*`);

  const isSelected = !!match;

  const { classes } = useMenuItemStyles({ isSelected });
  if (item.hidden) return <></>;
  return (
    <>
      {isExpanded && item.sectionDivider && <Divider className={classes.sectionDivider} />}
      {isExpanded && item.sectionTitle && (
        <Box sx={{ px: "16px", mb: "8px", mt: item.sectionDivider ? 0 : "18px" }}>
          <Typography className={classes.sectionTitle}>{item.sectionTitle}</Typography>
        </Box>
      )}
      <Box sx={{ paddingRight: isExpanded && "16px", paddingLeft: isExpanded && "16px" }}>
        <ListItemButton
          component={NavLink as any}
          key={item.href}
          to={item.href}
          disabled={item.enabled === false}
          onClick={() => {
            if (item.children.length < 1 && isSmall) {
              setIsExpanded(false);
            }
            useMixPanel(`navigate_to_${item.title}`);
          }}
          className={classes.listItem}
          disableGutters
          selected={!!match}
        >
          <>
            {item.icon && iconMap[item.icon] ? (
              <ListItemIcon
                classes={{
                  root: classes.iconRoot + (isExpanded ? "" : ` ${classes.iconRootCollapsed}`),
                }}
              >
                {renderIcon(iconMap[item.icon])}
              </ListItemIcon>
            ) : !item.enabled ? (
              iconMap.blocked
            ) : null}
            {!isSmall && isExpanded && (
              <Box display="flex" columnGap="6px">
                <ListItemText sx={{ margin: 0 }} primary={<span className={classes.title}>{item.title}</span>} />
                {newFeatureFlag && item.beta && <BadgeBetaFeature />}
              </Box>
            )}
          </>
          {/* {isSmall && !isExpanded && (item.icon || !item.enabled) ? null : (
            <ListItemText sx={{ margin: 0 }} primary={<span className={classes.title}>{item.title}</span>} />
          )} */}
        </ListItemButton>
        {isExpanded && item.children.length > 0 && (!!match || alwaysShowChildren) && (
          <div style={{ borderLeft: `4px solid ${theme.palette.primary.dark}` }}>
            <DrawerMenuSubItem ids={item.children} idMap={idMap} setIsExpanded={setIsExpanded} />
          </div>
        )}
      </Box>
    </>
  );
}
